import { FC, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingButton } from '@mui/lab';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import Papa from 'papaparse';
import { z } from 'zod';
import { generalStore } from '../../../stores/GeneralStore';
import { postSerialNumberFile } from '../../../api/stoApi';

interface Props {
  isOpen: boolean;
  assetId: string;
  closeDialog: () => void;
  openSuccessAlert: () => void;
}

export const SerialNumberploadDialog: FC<Props> = ({ isOpen, assetId, closeDialog, openSuccessAlert }) => {
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleCSVValidation = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsLoading(true);
    const clearState = (): void => {
      if (inputRef?.current) inputRef.current.value = '';
      setIsLoading(false);
    };
    const file: File | undefined = event?.target?.files?.[0];
    if (!file) return clearState();
    Papa.parse(file, {
      complete: async () => {
        try {
          await postSerialNumberFile(assetId, file);
          openSuccessAlert();
        } catch (err: any) {
          let errMsg = err?.message;
          if (err instanceof z.ZodError) {
            errMsg = t('dashboard.assets.notary.dates.dialog.error.invalidFormat');
          }
          generalStore.setError('Error: ' + errMsg);
        } finally {
          clearState();
          closeDialog();
        }
      },
      header: false,
    });
  };

  return (
    <Dialog open={isOpen} onClose={closeDialog} fullWidth maxWidth="sm">
      <DialogTitle>{t('dashboard.assets.serialNumber.title')}</DialogTitle>

      <DialogContent>
        <>
          <p style={{ whiteSpace: 'pre-line' }}>{t('dashboard.assets.serialNumber.dialog.description')}</p>
          <br />
          <i>
            <pre>
              10013232
              <br />
              10013233
              <br />
              10013234
            </pre>
          </i>
        </>
      </DialogContent>
      <DialogActions>
        <Button type="button" color="inherit" onClick={closeDialog}>
          {t('dashboard.assets.notary.dates.dialog.close')}
        </Button>
        <LoadingButton loading={isLoading} variant="contained" disableElevation color="primary" component="label">
          {t('dashboard.assets.notary.dates.dialog.upload')}
          <input ref={inputRef} hidden accept="csv/*" type="file" onChange={handleCSVValidation} />
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
